import { formatPhoneNumber, type ResidentApplicationData } from "@/types/application";
import { formatAddressDisplay } from "@/features/applicant/utils/addressHelpers";
import { formatCurrencyInput } from "@/lib/utils/formatters";
import { isPreScreeningInviteType } from "./applicantInviteType";
import type { ApplicationTableRow } from "@/features/applicant/types/utils";

function formatIncomeVerifiedDisplay(
  incomeVerified?: string | number | null,
  income?: string | number | null,
): string {
  if (incomeVerified === undefined || incomeVerified === null || String(incomeVerified).trim() === "") {
    return "";
  }
  const normalized = String(incomeVerified).trim();
  if (normalized === "1") {
    const incomeDisplay =
      income !== undefined && income !== null && String(income).trim() !== ""
        ? formatCurrencyInput(String(income), true)
        : "";
    return incomeDisplay ? `Yes (${incomeDisplay})` : "Yes";
  }
  if (normalized === "0") {
    return "No";
  }
  return normalized;
}

function formatLeaseDurationDisplay(leaseDuration?: string | number | null): string {
  if (leaseDuration === undefined || leaseDuration === null || String(leaseDuration).trim() === "") {
    return "";
  }
  const raw = String(leaseDuration).trim();
  if (/^\d+$/.test(raw)) {
    return `${raw} months`;
  }
  return raw;
}

export function mapApplicationToTableRow(app: ResidentApplicationData): ApplicationTableRow {
  let inviteDate = "";
  if (app.invite_created_date) {
    inviteDate = new Date(app.invite_created_date * 1000).toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  }

  const leaseStartDate = app.lease_start_date
    ? new Date(app.lease_start_date).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      })
    : "";

  const propertyName = app.property_name || "";

  let propertyAddress = app.physicalAddress || "";
  if (!propertyAddress && app.current_address) {
    propertyAddress = formatAddressDisplay(app.current_address);
  }

  const unitNumber = app.unit_number || "";
  const leaseDurationDisplay = formatLeaseDurationDisplay(app.lease_duration);
  const incomeVerifiedDisplay = formatIncomeVerifiedDisplay(app.income_verified, app.income);
  const rentAmountDisplay =
    app.rent_amount !== undefined && app.rent_amount !== null && String(app.rent_amount).trim() !== ""
      ? formatCurrencyInput(String(app.rent_amount), true)
      : "";
  const mobileNo = formatPhoneNumber(app.mobile_no || "");
  const status = app.status || app.application_status || app.invite_status || "";
  const activeButton = app.activeButton || "CONTINUE";
  const isApprovedRequire =
    app.is_approved_require !== undefined ? String(app.is_approved_require) : "1";

  return {
    inviteDate,
    propertyName,
    propertyAddress,
    unitNumber,
    leaseStartDate,
    leaseDurationDisplay,
    incomeVerifiedDisplay,
    rentAmountDisplay,
    mobileNo,
    status,
    activeButton,
    active: Boolean(
      app.active === true ||
        (app.active !== undefined && String(app.active) === "1") ||
        (app.active !== undefined && Number(app.active) === 1),
    ),
    applicantId: app.applicant_id,
    applicationId: app.fk_application_id || app.application_id,
    inviteId: app.invite_id,
    propertyId: app.property_id,
    isApprovedRequire,
    appInviteType: app.app_invite_type ?? null,
    originalApp: app,
  };
}

export function sortApplicationTableRows(
  rows: ApplicationTableRow[],
  sortColumn: string | null,
  sortOrder: "asc" | "desc",
): ApplicationTableRow[] {
  if (!sortColumn) {
    return rows;
  }

  return [...rows].sort((a, b) => {
    let aValue: string | number = "";
    let bValue: string | number = "";

    switch (sortColumn) {
      case "invite_date":
        aValue = a.inviteDate;
        bValue = b.inviteDate;
        break;
      case "lease_start_date":
        aValue = a.leaseStartDate;
        bValue = b.leaseStartDate;
        break;
      case "property_name":
        aValue = a.propertyName;
        bValue = b.propertyName;
        break;
      case "rent_amount":
        aValue = Number(a.originalApp.rent_amount ?? 0);
        bValue = Number(b.originalApp.rent_amount ?? 0);
        break;
      case "lease_duration":
        aValue = Number(a.originalApp.lease_duration ?? 0);
        bValue = Number(b.originalApp.lease_duration ?? 0);
        break;
      case "income_verified":
        aValue = Number(a.originalApp.income_verified ?? -1);
        bValue = Number(b.originalApp.income_verified ?? -1);
        break;
      case "unit_number":
        aValue = a.unitNumber;
        bValue = b.unitNumber;
        break;
      case "status":
        aValue = a.status;
        bValue = b.status;
        break;
      default:
        return 0;
    }

    if (sortOrder === "asc") {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });
}

export function partitionApplicationTableRows(rows: ApplicationTableRow[]): {
  applications: ApplicationTableRow[];
  preScreeningApplications: ApplicationTableRow[];
} {
  const applications: ApplicationTableRow[] = [];
  const preScreeningApplications: ApplicationTableRow[] = [];

  rows.forEach((row) => {
    if (isPreScreeningInviteType(row.appInviteType)) {
      preScreeningApplications.push(row);
    } else {
      applications.push(row);
    }
  });

  return { applications, preScreeningApplications };
}
